/**
 * CR500 Phase 2E: HeaderBlock
 * 
 * Renders the deal card header band with gradient background,
 * merchant logo, and optional MerchantNameBlock.
 * 
 * Extracted from DealGiftCard for v2 block-based rendering.
 * This is a PARITY implementation - matches V1 exactly.
 */

import React, { useMemo, useState } from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import type { MerchantNameStyle } from "@/lib/deals/globalThemeTokens";
import { resolveBrandLogo } from "@/lib/merchantLogoResolver";
import { MerchantNameBlock } from "@/components/deals/MerchantNameBlock";
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";
import { useDealCardUX, getHeaderHeightClass } from "@/contexts/DealCardUXContext";
import { getEffectiveVisibility, type VisibilityInputs } from "@/lib/deals/effectiveVisibility";
import { getDealType } from "@/lib/deals/getDealType";
import { cn } from "@/lib/utils";
import { 
  isProbablyBadBrand as isProbablyBadBrandHelper, 
  getDealCardTitle,
  getDealCardHeaderBrand,
  getDealCardHeaderContextMerchant,
} from "@/lib/deals/dealCardDisplay";

// ================================================================
// EXTENDED PROPS (beyond standard DealCardBlockProps)
// ================================================================

interface HeaderBlockProps extends DealCardBlockProps<Deal> {
  /**
   * Optional styling override for live preview (template editor).
   * When provided, uses inline styles instead of Tailwind classes.
   * 
   * CR306: For V2, visibility toggles should come from context.styleOverrides
   * (set via overrides_json.styleOverrides). This prop is for template editor preview only.
   */
  styleOverride?: {
    headerHeight?: "sm" | "md" | "lg";
    headerBackgroundType?: "solid" | "gradient";
    headerBackgroundColor?: string;
    headerGradientFrom?: string;
    headerGradientTo?: string;
    showMerchantLogo?: boolean;
    showMerchantName?: boolean;
  };
  
  /**
   * Template sections for visibility resolution.
   */
  templateSections?: Record<string, boolean>;
  
  /**
   * Override for header height (px) from template.
   */
  headerHeightPx?: string;
  
  /**
   * Override for header gradient colors.
   */
  headerGradientFrom?: string;
  headerGradientTo?: string;
  
  /**
   * Override for header text color.
   */
  headerTextColor?: string;
  
  /**
   * Whether template override is active (controls inline vs Tailwind styles).
   */
  templateOverrideActive?: boolean;
}

// ================================================================
// HELPERS
// ================================================================

/**
 * CR412: Derive the header brand name (issuer-first).
 * 
 * Priority:
 * 1. Server-computed header_brand_name (from edge function, issuer-first)
 * 2. issuer_display_name (from enriched view / merchants join)
 * 3. Client-side fallback using centralized helper
 */
function deriveHeaderBrandName(deal: Deal): string {
  // 1. Server-computed field takes priority (already issuer-first in edge function)
  const serverBrand = (deal as any).header_brand_name as string | null | undefined;
  if (serverBrand) return serverBrand;

  // 2. CR412: issuer_display_name from enriched view join
  if (deal.issuer_display_name) return deal.issuer_display_name;

  // 3. Client-side fallback using centralized helper
  const resolved = getDealCardHeaderBrand(deal);
  return resolved.name ?? getDealCardTitle(deal);
}

/**
 * CR417: Derive the brand key for logo resolution (matches header brand).
 * 
 * Priority:
 * 1. Resolved issuer (from merchant directory join)
 * 2. purchase_gift_card_brand (gift card brand being promoted)
 * 3. gift_card_brand (legacy)
 * 4. Purchase merchant (last resort)
 */
function deriveHeaderLogoBrand(deal: Deal): string | null {
  // CR-Issuer: If issuer is resolved, use issuer merchant key for logo
  if (deal.issuer_merchant_key) {
    return deal.issuer_display_name ?? deal.issuer_merchant_key;
  }
  // Use the same brand as the header text (issuer-first)
  const pgcb = deal.purchase_gift_card_brand;
  if (pgcb && !isProbablyBadBrandHelper(pgcb)) return pgcb;
  // CR418: gift_card_brand (legacy), NOT purchase_gift_card_brand again
  const gcBrand = deal.gift_card_brand;
  if (gcBrand && !isProbablyBadBrandHelper(gcBrand)) return gcBrand;
  // CR418: reward_brand_name before merchant fallback
  const rewardBrand = deal.reward_brand_name;
  if (rewardBrand && !isProbablyBadBrandHelper(rewardBrand)) return rewardBrand;
  // Fall back to merchant — CR418: use merchant (Deal type field name)
  const merchant = deal.promo_merchant_name ?? deal.merchant;
  if (merchant && !isProbablyBadBrandHelper(merchant)) return merchant;
  return null;
}

// ================================================================

// ================================================================
// COMPONENT
// ================================================================

export const HeaderBlock: React.FC<HeaderBlockProps> = ({
  deal,
  block,
  context,
  styleOverride,
  templateSections,
  headerHeightPx: propHeightPx,
  headerGradientFrom: propGradientFrom,
  headerGradientTo: propGradientTo,
  headerTextColor: propTextColor,
  templateOverrideActive = false,
}) => {
  const [logoError, setLogoError] = useState(false);
  
  // Global theme for fallback values
  const { styles: globalStyles, tokens: globalTokens } = useGlobalTheme();
  
  // Card UX context for density settings
  const { cardSize } = useDealCardUX();
  const headerHeightClass = getHeaderHeightClass(cardSize);
  
  // Resolve deal category
  const { category: dealCategory } = getDealType(deal);
  
  // CR417: Derive header brand (issuer-first) and logo brand
  const displayTitle = deriveHeaderBrandName(deal);
  const purchaseBrand = deriveHeaderLogoBrand(deal);
  
  // CR306: V2 reads visibility from context.styleOverrides (from overrides_json)
  // Template editor preview passes styleOverride prop which takes precedence
  const resolvedStyleOverrides = context.styleOverrides || {};
  
  // Build visibility inputs and get effective visibility
  // All hooks MUST be called before any conditional returns
  const visibilityInputs: VisibilityInputs = useMemo(() => ({
    styling: {
      // CR306: Priority: prop override > context.styleOverrides > default
      showMerchantLogo: styleOverride?.showMerchantLogo 
        ?? (resolvedStyleOverrides.showMerchantLogo as boolean | undefined),
      showMerchantName: styleOverride?.showMerchantName 
        ?? (resolvedStyleOverrides.showMerchantName as boolean | undefined),
    },
    sections: templateSections as VisibilityInputs["sections"],
    globalTokens: {
      components: {
        merchantName: {
          placement: globalTokens?.components?.merchantName?.placement,
          style: globalTokens?.components?.merchantName?.style,
        },
      },
    },
    surface: context.isAdmin ? "admin" : "public",
    isAdmin: context.isAdmin,
  }), [styleOverride, resolvedStyleOverrides, templateSections, globalTokens, context.isAdmin]);
  
  const effectiveVisibility = useMemo(
    () => getEffectiveVisibility(visibilityInputs),
    [visibilityInputs]
  );
  
  const {
    showMerchantLogo: shouldShowMerchantLogo,
    showMerchantInHeader,
    merchantNameStyle,
  } = effectiveVisibility;
  
  // Resolve logo - CR-Issuer: prefer issuer_logo_url from directory join
  const issuerLogoUrl = deal.issuer_logo_url;
  const purchaseLogoResult = useMemo(
    () => resolveBrandLogo(purchaseBrand),
    [purchaseBrand]
  );
  
  // Compute header styles (inline or Tailwind)
  const headerHeightPx = propHeightPx || globalStyles.headerHeight;
  
  const headerGradientFrom = useMemo(() => {
    if (propGradientFrom) return propGradientFrom;
    if (styleOverride?.headerGradientFrom) {
      const val = styleOverride.headerGradientFrom;
      return val.includes("hsl") || val.includes("rgba") ? val : `hsl(${val})`;
    }
    return globalStyles.headerBackground;
  }, [propGradientFrom, styleOverride?.headerGradientFrom, globalStyles.headerBackground]);
  
  const headerGradientTo = useMemo(() => {
    if (propGradientTo) return propGradientTo;
    if (styleOverride?.headerGradientTo) {
      const val = styleOverride.headerGradientTo;
      return val.includes("hsl") || val.includes("rgba") ? val : `hsl(${val})`;
    }
    return globalStyles.headerBackground;
  }, [propGradientTo, styleOverride?.headerGradientTo, globalStyles.headerBackground]);
  
  const headerTextColor = propTextColor || globalStyles.headerText;
  
  // CR-Issuer: Determine logo source - prefer issuer URL, fallback to brand resolver
  const hasIssuerLogo = !logoError && !!issuerLogoUrl;
  const hasBrandLogo = !logoError && (purchaseLogoResult.svg || purchaseLogoResult.src);
  const hasValidLogo = hasIssuerLogo || hasBrandLogo;
  const shouldRenderLogo = shouldShowMerchantLogo && hasValidLogo;
  
  // CR305B: Determine if we should show merchant name in header
  // Use displayTitle instead of purchaseBrand for the name display
  const hasValidTitle = displayTitle && displayTitle !== "Deal";
  const shouldRenderMerchantName = showMerchantInHeader && hasValidTitle;
  
  // CR-Issuer: Get logo src - prefer issuer URL, fallback to brand resolver
  const logoSrc = hasIssuerLogo
    ? issuerLogoUrl
    : (purchaseLogoResult.svg 
      ? `data:image/svg+xml,${encodeURIComponent(purchaseLogoResult.svg)}`
      : purchaseLogoResult.src);
  
  // Don't render if block is disabled (AFTER all hooks)
  if (!block.enabled) {
    return null;
  }
  
  return (
    <div
      data-block="header"
      data-block-id={block.id}
      data-component-key="header"
      className={cn(
        "relative flex flex-shrink-0 items-center gap-3 overflow-hidden rounded-t-[calc(var(--wi-card-radius,24px)-1px)] px-5 pr-32",
        // Use Tailwind classes when no template override
        !templateOverrideActive && headerHeightClass,
      )}
      style={{
        // CR307: Use CSS vars from ThemePreviewWrapper when available
        // Falls back to computed values for compatibility
        height: templateOverrideActive ? headerHeightPx : "var(--wi-header-height, 5rem)",
        background: templateOverrideActive 
          ? `linear-gradient(to right, ${headerGradientFrom}, ${headerGradientTo})`
          : `linear-gradient(to right, var(--wi-header-bg, ${headerGradientFrom}), var(--wi-header-bg, ${headerGradientTo}))`,
        color: `var(--wi-header-text, ${headerTextColor})`,
        borderBottom: "1px solid var(--wi-color-border-default, #e4e4e7)",
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />

      {/* Merchant/Brand Logo */}
      {shouldRenderLogo && logoSrc && (
        <img
          src={logoSrc}
          alt={purchaseBrand ?? "Brand"}
          className="h-10 md:h-12 object-contain max-w-[120px] flex-shrink-0"
          onError={() => setLogoError(true)}
        />
      )}
      
      {/* CR305B: Merchant/Brand Name (when placement is HEADER_TITLE) */}
      {/* Uses displayTitle from centralized helper for correct brand priority */}
      {shouldRenderMerchantName && (
        <MerchantNameBlock
          merchantName={displayTitle}
          style={merchantNameStyle}
          context="header"
          textColor={headerTextColor}
        />
      )}
    </div>
  );
};

export default HeaderBlock;
